import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create a custom fetch with retry logic
const fetchWithRetry = async (url: string, options: RequestInit, retries = 3, delay = 1000) => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay * 2);
    }
    throw error;
  }
};

// Regular client for normal operations
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    fetch: fetchWithRetry
  }
});

// Admin client for privileged operations
export const adminSupabase = supabaseServiceKey 
  ? createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: false
      }
    })
  : null;

// Add a helper to check connection
export const checkSupabaseConnection = async () => {
  try {
    const { error } = await supabase.from('books').select('count', { count: 'exact', head: true });
    return !error;
  } catch {
    return false;
  }
};

// User role helpers
export const getUserRole = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('user_id', user.id)
    .single();

  if (error || !data) return null;
  return data.role;
};

export const isAdmin = async () => {
  const role = await getUserRole();
  return role === 'admin';
};

export const isAuthor = async () => {
  const role = await getUserRole();
  return role === 'admin' || role === 'author';
};

export const isReader = async () => {
  const role = await getUserRole();
  return role === 'reader';
};

// Error handling utility
export const handleSupabaseError = (error: unknown): string => {
  if (error instanceof Error) {
    // Handle specific Supabase error codes
    if ('code' in error) {
      switch (error.code) {
        case '23505': // Unique violation
          return 'This value already exists. Please try another.';
        case '23503': // Foreign key violation
          return 'This operation cannot be completed because it references non-existent data.';
        case '42P01': // Undefined table
          return 'Database configuration error. Please contact support.';
        case '42501': // Insufficient privilege
          return 'You do not have permission to perform this action.';
        default:
          return error.message;
      }
    }
    return error.message;
  }
  return 'An unexpected error occurred';
};